/**
 * Aviso de **portal movido** (`/portal-cliente`).
 *
 * El portal del cliente dejó de vivir dentro del panel: ahora es una aplicación
 * aparte (`edsuite-portal`, en su propio subdominio/puerto) para que el cliente
 * no descargue nada del panel. Esta pantalla existe sólo para que los enlaces
 * antiguos (`…/portal-cliente`) no terminen en un 404 o en el login del panel, y
 * lleva al cliente al acceso nuevo.
 *
 * La dirección se configura con `VITE_PORTAL_URL` (ver `config/env.ts`).
 */

import { ArrowUpRight, KeyRound, ShieldCheck } from 'lucide-react';

import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { env } from '@/config/env';
import { useDocumentTitle } from '@/hooks/use-document-title';

export function PortalMovidoPage() {
  useDocumentTitle('Portal del cliente');

  return (
    <div className="flex min-h-dvh items-center justify-center bg-slate-50 px-4 py-10">
      <Card className="w-full max-w-lg p-6 sm:p-8">
        <p className="font-mono text-[11px] tracking-widest text-brand-600 uppercase">EDSuite</p>
        <h1 className="mt-2 text-xl font-bold tracking-tight text-ink-900">
          El portal del cliente está en otra dirección
        </h1>
        <p className="mt-2 text-sm text-ink-500">
          El acceso de clientes ya no forma parte del panel: es una aplicación aparte, con su
          propia dirección, para que cada uno vea sólo lo suyo.
        </p>

        {env.portalUrl ? (
          <>
            <Button
              className="mt-5"
              size="lg"
              rightIcon={<ArrowUpRight className="size-4" aria-hidden="true" />}
              onClick={() => {
                window.location.href = env.portalUrl;
              }}
            >
              Ir al portal del cliente
            </Button>
            <p className="mt-2 font-mono text-xs text-ink-400">{env.portalUrl}</p>
          </>
        ) : (
          <Alert variant="info" className="mt-5">
            Pide a soporte la dirección del portal del cliente: en esta instalación todavía no
            está publicada.
          </Alert>
        )}

        <div className="mt-6 flex flex-col gap-2 border-t border-ink-100 pt-4 text-xs text-ink-500">
          <span className="flex items-center gap-2">
            <KeyRound className="size-3.5 shrink-0 text-ink-400" aria-hidden="true" />
            El cliente entra con su <strong className="font-semibold">documento</strong> y la
            contraseña que se genera desde su ficha en el panel.
          </span>
          <span className="flex items-center gap-2">
            <ShieldCheck className="size-3.5 shrink-0 text-ink-400" aria-hidden="true" />
            Si eres operador, este aviso no afecta a tu sesión: el panel sigue igual.
          </span>
        </div>
      </Card>
    </div>
  );
}
